import React, { useState, useEffect } from 'react';
import { Box, List, ListItemText, Checkbox, TableBody, TableCell, Table, CircularProgress, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  MainContainer,
  DescriptionText,
  LayoutGrid,
  RolesPanel,
  RolesTitle,
  RoleItem,
  MappingPanel,
  MappingTitle,
  StyledTableContainer,
  StyledTableHead,
  StyledTableRow,
  SaveButton
} from './styles';
import { useBreadcrumb } from '../../../../context/BreadcrumbContext';
import { useSidebar } from '../../../../context/SidebarContext';
import {
  fetchOrgRoles,
  fetchSubscribedModules,
  fetchRoleMappings,
  saveRoleModuleMapping,
  resetAccessControlActionState,
} from '../../../../modules/accessControl';
import {
  selectOrgRoles,
  selectSubscribedModules,
  selectRoleMappings,
  selectRoleMappingsLoading,
  selectSaveMappingLoading,
  selectSaveMappingSuccess,
  selectSaveMappingError,
} from '../../../../modules/accessControl/selectors';
import { AppDispatch } from '../../../../store';
import AlertPopup from '../../../../components/common/AlertPopup';

const RoleMapping: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { setActivePage } = useSidebar();
  const { setPath, setIsShow } = useBreadcrumb();

  // Redux Selectors
  const roles = useSelector(selectOrgRoles);
  const modules = useSelector(selectSubscribedModules);
  const existingMappings = useSelector(selectRoleMappings);
  const isSaving = useSelector(selectSaveMappingLoading);
  const saveSuccess = useSelector(selectSaveMappingSuccess);
  const saveError = useSelector(selectSaveMappingError);
  const isLoadingMapping = useSelector(selectRoleMappingsLoading);

  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [localMappings, setLocalMappings] = useState<Record<number, any>>({});
  const [alert, setAlert] = useState<{ message: string, severity: 'success' | 'error' } | null>(null);

  useEffect(() => {
    setActivePage('org-role-mapping');
    setPath([
      { title: 'Access Control', path: '/access-control/roles' },
      { title: 'Role Permissions', path: '/access-control/mapping' },
    ]);
    setIsShow(true);

    dispatch(fetchOrgRoles());
    dispatch(fetchSubscribedModules());

    return () => {
      dispatch(resetAccessControlActionState());
    }
  }, [setActivePage, setPath, setIsShow, dispatch]);

  useEffect(() => {
    if (roles.length > 0 && selectedRoleId === null) {
      setSelectedRoleId(roles[0].role_id);
    }
  }, [roles, selectedRoleId]);

  useEffect(() => {
    if (selectedRoleId) {
      dispatch(fetchRoleMappings(selectedRoleId));
    }
  }, [selectedRoleId, dispatch]);

  useEffect(() => {
    if (existingMappings && Array.isArray(existingMappings)) {
      const mappingObj: Record<number, any> = {};
      existingMappings.forEach((m: any) => {
        const moduleId = m.system_module?.module_id || m.system_module;
        if (moduleId) {
          mappingObj[moduleId] = m;
        }
      });
      setLocalMappings(mappingObj);
    }
  }, [existingMappings]);

  useEffect(() => {
    if (saveSuccess) {
      setAlert({ message: 'Permissions saved successfully', severity: 'success' });
      dispatch(resetAccessControlActionState());
    }
    if (saveError) {
      setAlert({ message: saveError, severity: 'error' });
      dispatch(resetAccessControlActionState());
    }
  }, [saveSuccess, saveError, dispatch]);

  const handleRoleClick = (id: number) => {
    setSelectedRoleId(id);
  };

  const handlePermissionToggle = (moduleId: number, permission: string) => {
    setLocalMappings(prev => {
      const current = prev[moduleId] || {
        have_read: false,
        have_write: false,
        have_edit: false,
        have_delete: false,
        have_admin: false,
        system_module: moduleId,
        system_role: selectedRoleId
      };

      const newValue = !current[permission];
      let updatedPermissions = { ...current, [permission]: newValue };

      if (permission === 'have_admin' && newValue) {
        updatedPermissions = {
          ...updatedPermissions,
          have_read: true,
          have_write: true,
          have_edit: true,
          have_delete: true,
        };
      }

      return {
        ...prev,
        [moduleId]: updatedPermissions
      };
    });
  };

  const handleSave = async () => {
    if (!selectedRoleId) return;

    try {
      const promises = Object.values(localMappings).map((mapping: any) => {
        return dispatch(saveRoleModuleMapping({
          role_id: selectedRoleId,
          module_id: mapping.system_module?.module_id || mapping.system_module,
          have_read: mapping.have_read,
          have_write: mapping.have_write,
          have_edit: mapping.have_edit,
          have_delete: mapping.have_delete,
          have_admin: mapping.have_admin,
        }));
      });

      await Promise.all(promises);
      dispatch(fetchRoleMappings(selectedRoleId));
    } catch (error) {
      setAlert({ message: 'Failed to update mappings', severity: 'error' });
    }
  };

  return (
    <MainContainer>
      {alert && <AlertPopup message={alert.message} severity={alert.severity} open={!!alert} onClose={() => setAlert(null)} />}
      <DescriptionText>
        Configure module permissions for your organization staff.
      </DescriptionText>

      <LayoutGrid>
        <RolesPanel>
          <RolesTitle>Organization Roles</RolesTitle>
          <List component="nav" disablePadding>
            {Array.isArray(roles) && roles.map((role: any) => (
              <RoleItem
                key={role.role_id}
                selected={selectedRoleId === role.role_id}
                onClick={() => handleRoleClick(role.role_id)}
              >
                <ListItemText primary={role.role_name} />
              </RoleItem>
            ))}
          </List>
          {roles.length === 0 && <Typography sx={{ p: 2, fontSize: '0.875rem' }}>No roles defined.</Typography>}
        </RolesPanel>

        <MappingPanel>
          <MappingTitle>
            Module Permissions
            {isLoadingMapping && <CircularProgress size={20} sx={{ ml: 2 }} />}
          </MappingTitle>

          <StyledTableContainer>
            <Table size="small">
              <StyledTableHead>
                <StyledTableRow>
                  <TableCell>Module Name</TableCell>
                  <TableCell align="center">Read</TableCell>
                  <TableCell align="center">Write</TableCell>
                  <TableCell align="center">Edit</TableCell>
                  <TableCell align="center">Delete</TableCell>
                  <TableCell align="center">Admin</TableCell>
                </StyledTableRow>
              </StyledTableHead>
              <TableBody>
                {Array.isArray(modules) && modules.map((module: any) => {
                  const mapping = localMappings[module.module_id] || {};
                  return (
                    <StyledTableRow key={module.module_id}>
                      <TableCell>{module.module_name}</TableCell>
                      <TableCell align="center">
                        <Checkbox
                          size="small"
                          checked={!!mapping.have_read}
                          onChange={() => handlePermissionToggle(module.module_id, 'have_read')}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          size="small"
                          checked={!!mapping.have_write}
                          onChange={() => handlePermissionToggle(module.module_id, 'have_write')}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          size="small"
                          checked={!!mapping.have_edit}
                          onChange={() => handlePermissionToggle(module.module_id, 'have_edit')}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          size="small"
                          checked={!!mapping.have_delete}
                          onChange={() => handlePermissionToggle(module.module_id, 'have_delete')}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          size="small"
                          checked={!!mapping.have_admin}
                          onChange={() => handlePermissionToggle(module.module_id, 'have_admin')}
                        />
                      </TableCell>
                    </StyledTableRow>
                  );
                })}
                {modules.length === 0 && (
                   <StyledTableRow>
                     <TableCell colSpan={6} align="center">No modules found in your subscription plan.</TableCell>
                   </StyledTableRow>
                )}
              </TableBody>
            </Table>
          </StyledTableContainer>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <SaveButton
              variant="contained"
              disableElevation
              onClick={handleSave}
              disabled={isSaving || !selectedRoleId || modules.length === 0}
            >
              {isSaving ? 'Saving...' : 'Save Permissions'}
            </SaveButton>
          </Box>
        </MappingPanel>
      </LayoutGrid>
    </MainContainer>
  );
};

export default RoleMapping;
